import BillingModel from "../../../types/billing-model";
import BillingsNode from "../../../common/types/billings-node";
import googleMapsRouteService from "../../../../../common/utils/google-maps-route/google-maps-route.service";
import TabsData from "../../../../../common/components/tabs/common/types/tabs-data";
import billingsTranslationsHelper from "../../../../../languages/billings-translations.helper";
import BillingsCargoTaxiContributionsDiscountTableComponent from "../../common/contributions/discount/table/billings-cargo-taxi-contributions-discount-table.component";
import BillingsCargoTaxiContributionsDiscountTableRow from "../../common/contributions/discount/types/billings-cargo-taxi-contributions-discount-table-row";
import BillingsCargoTaxiContribution, {
  BillingsCargoTaxiContributionContractCorrectionPercentage,
  BillingsCargoTaxiContributionDiscountBeingLate,
  BillingsCargoTaxiContributionDiscountIncompatibleCar,
  BillingsCargoTaxiContributionDiscountOptimization,
  BillingsCargoTaxiContributionDiscountOther,
  BillingsCargoTaxiContributionDiscountType,
} from "../../common/contributions/discount/types/billings-cargo-taxi-contributions-discount";
import BillingFormData, {
  BillingFormDataContributions,
} from "../types/billing-form.data";
import billingsCargoTaxiContributionsDiscountHelper from "../../common/contributions/discount/billings-cargo-taxi-contributions-discount.helper";
import BillingsCargoTaxiDetails, {
  BillingsCargoTaxiDetailsBillingContribution,
  BillingsCargoTaxiDetailsBillingContributionContractCorrectionPercentage,
  BillingsCargoTaxiDetailsBillingContributionDiscountBeingLate,
  BillingsCargoTaxiDetailsBillingContributionDiscountIncompatibleCar,
  BillingsCargoTaxiDetailsBillingContributionDiscountOptimization,
  BillingsCargoTaxiDetailsBillingContributionDiscountOther,
  BillingsCargoTaxiDetailsBillingContributionType,
  BillingsCargoTaxiDetailsBillingNode,
  BillingsCargoTaxiDetailsContract,
} from "../../../../../common/services/billings/cargo-taxi/details/billings-cargo-taxi-details";
import BillingStatus from "../../../types/billing-status";
import ContractDetails from "../types/contract-details";

const createBillingNode = (
  node: BillingsCargoTaxiDetailsBillingNode,
  index: number,
  nodes: BillingsCargoTaxiDetailsBillingNode[],
) => {
  const previousItem = index - 1 >= 0 ? nodes[index - 1] : null;

  const url =
    previousItem !== null
      ? googleMapsRouteService.createGoogleMapsRouteUrl([
          { latitude: previousItem.lat, longitude: previousItem.lon },
          { latitude: node.lat, longitude: node.lon },
        ])
      : "";

  const result: BillingsNode = {
    id: node.uuid,
    nodeId: node.nodeUuid,
    lat: node.lat,
    lon: node.lon,
    description: node.description,
    checkoutDate: node.checkoutDate ?? null,
    checkoutEventUuid: node.checkoutEventUuid ?? null,
    plannedDate: node.plannedDate ?? null,
    plannedDistance: node.plannedDistance ?? null,
    orderId: node.orderUuid ?? null,
    distance: node.distance,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    isHighwayAllowed: node.allowCharge,
    isEditable: node.editableCoordinates,
    googleMapsUrl: url,
    position: index + 1,
  };

  return result;
};

const createBillingNodes = (
  nodes: BillingsCargoTaxiDetailsBillingNode[],
): BillingsNode[] => {
  return nodes.map(createBillingNode);
};

const createBillingDiscountContributionType = (
  contributionType:
    | BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_BEING_LATE
    | BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR
    | BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OPTIMIZATION
    | BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OTHER,
):
  | BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE
  | BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR
  | BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION
  | BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER => {
  switch (contributionType) {
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_BEING_LATE:
      return BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE;
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR:
      return BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR;
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OPTIMIZATION:
      return BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION;
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OTHER:
      return BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER;
  }
};

const createBillingDiscountContribution = (
  contribution:
    | BillingsCargoTaxiDetailsBillingContributionContractCorrectionPercentage
    | BillingsCargoTaxiDetailsBillingContributionDiscountBeingLate
    | BillingsCargoTaxiDetailsBillingContributionDiscountIncompatibleCar
    | BillingsCargoTaxiDetailsBillingContributionDiscountOptimization
    | BillingsCargoTaxiDetailsBillingContributionDiscountOther,
):
  | BillingsCargoTaxiContributionContractCorrectionPercentage
  | BillingsCargoTaxiContributionDiscountOptimization
  | BillingsCargoTaxiContributionDiscountBeingLate
  | BillingsCargoTaxiContributionDiscountIncompatibleCar
  | BillingsCargoTaxiContributionDiscountOther => {
  switch (contribution.type) {
    case BillingsCargoTaxiDetailsBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE:
      return {
        type: BillingsCargoTaxiContributionDiscountType.CONTRACT_CORRECTION_PERCENTAGE,
        percentage: contribution.percentage,
        amount: contribution.amount,
      };
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_BEING_LATE:
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR:
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OPTIMIZATION:
    case BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OTHER:
      return {
        type: createBillingDiscountContributionType(contribution.type),
        comment: contribution.comment ?? "",
        amount: contribution.amount,
      };
  }
};

const createBillingContributions = (
  contributions: BillingsCargoTaxiDetailsBillingContribution[],
): BillingFormDataContributions => {
  const discountContribution = contributions.filter(
    (contribution) =>
      contribution &&
      [
        BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_BEING_LATE,
        BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR,
        BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OPTIMIZATION,
        BillingsCargoTaxiDetailsBillingContributionType.DISCOUNT_OTHER,
        BillingsCargoTaxiDetailsBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE,
      ].includes(contribution.type),
  ) as (
    | BillingsCargoTaxiDetailsBillingContributionContractCorrectionPercentage
    | BillingsCargoTaxiDetailsBillingContributionDiscountBeingLate
    | BillingsCargoTaxiDetailsBillingContributionDiscountIncompatibleCar
    | BillingsCargoTaxiDetailsBillingContributionDiscountOptimization
    | BillingsCargoTaxiDetailsBillingContributionDiscountOther
  )[];

  return {
    discount: discountContribution.map((contribution) =>
      createBillingDiscountContribution(contribution),
    ),
  };
};

const createContract = (
  contract: BillingsCargoTaxiDetailsContract,
): ContractDetails => {
  return {
    companyName: contract.cargoCompany.displayName,
    discountLimit: contract.discountLimit,
    haltingTimeRate: contract.haltingTimeRate,
    distanceRate: contract.distanceRate,
    isHighwayAllowedDuringRide: contract.allowChargeDuringRide,
    isHighwayAllowedWhenApproaching: contract.allowChargeWhileApproaching,
    isHighwayAllowedWhileReturning: contract.allowChargeWhileReturning,
    model: contract.model,
    haltingTimeAppliedAfter: contract.haltingTimeAfterMinutes,
  };
};

const createBillingData = (data: BillingsCargoTaxiDetails) => {
  const billingDraft: BillingFormData = {
    billingNodes: createBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
    contractDetails: createContract(data.contract),
    baseAmountValue: data.baseAmountValue,
    sumOfDiscounts: data.sumOfDiscounts,
    amountForChargeHaltings: data.amountForChargeHaltings,
    amountForChargeHighways: data.amountForChargeHighways,
    distance: data.distance,
    amountForDistance: data.amountForDistance,
    internalOrderId: data.cargoOrder.humanId,
    allContributionsAmount: data.allContributionsAmount,
    planEntryId: data.planEntryUuid,
    date: data.date,
    status: data.status,
    passengers: data.passengers,
    contractCorrectionPercentage: data.contractCorrectionPercentage,
  };

  return billingDraft;
};

const createContributionsDiscountTableRow = (
  contribution: BillingsCargoTaxiContribution,
  contributionIndex: number,
): BillingsCargoTaxiContributionsDiscountTableRow => {
  const contributionType =
    billingsCargoTaxiContributionsDiscountHelper.getContributionTypeTranslation(
      contribution.type,
    );

  const translations =
    billingsTranslationsHelper.getCargoTaxiContributionsTranslations();

  switch (contribution.type) {
    case BillingsCargoTaxiContributionDiscountType.CONTRACT_CORRECTION_PERCENTAGE:
      return {
        id: `${contribution.type}-${contributionIndex}`,
        value: {
          type: <div title={contributionType}>{contributionType}</div>,
          amount: (
            <div title={String(contribution.amount)}>{contribution.amount}</div>
          ),
          comment: (
            <div title={translations.attributeNotApplicable}>
              {translations.attributeNotApplicable}
            </div>
          ),
          percentage: (
            <div title={String(contribution.percentage)}>
              {contribution.percentage}
            </div>
          ),
          actions: <div className="d-flex"></div>,
        },
      };
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER:
      return {
        id: `${contribution.type}-${contributionIndex}`,
        value: {
          type: <div title={contributionType}>{contributionType}</div>,
          amount: (
            <div title={String(contribution.amount)}>{contribution.amount}</div>
          ),
          comment: (
            <div title={String(contribution.comment)}>
              {contribution.comment}
            </div>
          ),
          percentage: (
            <div title={translations.attributeNotApplicable}>
              {translations.attributeNotApplicable}
            </div>
          ),
          actions: <div className="d-flex"></div>,
        },
      };
    default:
      return {
        id: String(contributionIndex),
        value: {
          type: <div></div>,
          amount: <div></div>,
          comment: <div></div>,
          percentage: <div></div>,
          actions: <div className="d-flex"></div>,
        },
      };
  }
};

const createTabsData = (
  contributions: BillingFormDataContributions,
  isDataLoading: boolean,
): TabsData => {
  const translations =
    billingsTranslationsHelper.getCargoTaxiContributionsTranslations();

  const discountTableRows: BillingsCargoTaxiContributionsDiscountTableRow[] =
    contributions.discount.map((contribution, index) =>
      createContributionsDiscountTableRow(contribution, index),
    );

  const DiscountTabContent = (
    <BillingsCargoTaxiContributionsDiscountTableComponent
      isLoading={isDataLoading}
      rows={discountTableRows}
    />
  );

  const tabsData: TabsData = [
    {
      label: translations.discount.title,
      content: DiscountTabContent,
      counter: contributions.discount.filter(
        (contribution) => contribution.type,
      ).length,
      totalTableRows: discountTableRows.length,
    },
  ];

  return tabsData;
};

const billingDataFactory = {
  createBillingData,
  createTabsData,
};

export default billingDataFactory;
